// Production orders — data access (docs/design/production.md, ADR-029).
//
//   list     rpc production_order_page(company, status, term, after_date, after_number, limit 51)
//            keyset (start_date desc, number desc) on production_orders_open_idx / _status_idx /
//            _list_idx; search: number prefix or manufactured item (trigram → item index).
//   detail   rpc production_order_document(id) — header, materials with reservation and
//            availability, operations; one round-trip.
//   writes   save_ / release_ / reserve_ / cancel_ / complete_ / close_production_order.

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import type { Page } from "@/components/DataList";
import { isDemoMode, supabase } from "@/lib/supabase";
import { sortParam, type ListFilter, type ListSort } from "@patronix/domain/list";

export const productionOrderStatuses = ["planned", "released", "in_progress", "completed", "closed", "cancelled"] as const;
export type ProductionOrderStatus = (typeof productionOrderStatuses)[number];
export type OrderStatusFilter = "open" | "all" | ProductionOrderStatus;
export const isOrderStatusFilter = (v: unknown): v is OrderStatusFilter =>
  v === "open" || v === "all" || (productionOrderStatuses as readonly unknown[]).includes(v);

export const orderTone = {
  planned: "neutral",
  released: "info",
  in_progress: "warning",
  completed: "success",
  closed: "neutral",
  cancelled: "danger",
} as const satisfies Record<ProductionOrderStatus, string>;

export const holdsMaterialReservations = (s: ProductionOrderStatus) => s === "released" || s === "in_progress";

export interface ProductionOrderRow {
  id: string;
  number: string;
  status: ProductionOrderStatus;
  item_id: string;
  item_code: string;
  item_description: string;
  planned_qty: string | number;
  reported_good_qty: string | number;
  base_uom_code: string;
  start_date: string;
  due_date: string;
  short_line_count: number;
  warehouse_code: string;
}

export interface MaterialLine {
  id: string;
  line_no: number;
  item_id: string;
  item_code: string;
  item_description: string;
  lot_controlled: boolean;
  serial_controlled?: boolean;
  base_uom_code: string;
  base_uom_decimals: number;
  qty_per_unit: string | number;
  scrap_pct: string | number;
  required_base_qty: string | number;
  reserved_base_qty: string | number;
  issued_base_qty: string | number;
  net_qty_per_unit: string | number;
  issue_method: "manual" | "backflush";
  operation_seq: number | null;
  warehouse_id: string;
  warehouse_code: string;
  supply_location_code: string | null;
  status: "open" | "issued" | "closed";
  available_base_qty: string | number | null;
}

export interface OperationLine {
  id: string;
  seq: number;
  work_center_id: string;
  work_center_code: string;
  work_center_name: string;
  description: string;
  planned_setup_minutes: string | number;
  planned_run_minutes: string | number;
  good_qty: string | number;
  scrap_qty: string | number;
  actual_minutes: string | number;
  status: "open" | "started" | "done";
  work_center_kind?: string;
  /** Subcontracted operation: the PO line buying it (null until released). */
  subcontract?: {
    supplier_name: string | null;
    line_id: string | null;
    po_id: string | null;
    po_number: string | null;
    po_status: string | null;
    ordered_qty: string | number | null;
    received_qty: string | number | null;
    promised_date: string | null;
    /** The subcontractor warehouse its materials are supplied from. */
    warehouse_code?: string | null;
    /** Materials of the operation and how much was sent there for it. */
    materials?: SubcontractMaterial[];
  } | null;
}

export interface SubcontractMaterial {
  material_id: string;
  item_code: string;
  item_description: string;
  required_qty: string | number;
  issued_qty: string | number;
  reserved_qty: string | number;
  sent_qty: string | number;
  uom_code: string;
  uom_decimals: number;
}

export interface ProductionOrderDocument {
  id: string;
  number: string;
  /** Project peg; materials and output use that project's stock (PR 3a/3b). */
  project_id?: string | null;
  project_code?: string | null;
  status: ProductionOrderStatus;
  company_id: string;
  item_id: string;
  item_code: string;
  item_description: string;
  lot_controlled: boolean;
  serial_controlled?: boolean;
  base_uom_code: string;
  base_uom_decimals: number;
  over_report_pct: string | number;
  entered_qty: string | number;
  uom_id: string;
  uom_code: string;
  planned_qty: string | number;
  bom_id: string;
  bom_version: number;
  bom_pinned: boolean;
  routing_id: string | null;
  routing_version: number | null;
  warehouse_id: string;
  warehouse_code: string;
  receipt_location_id: string | null;
  receipt_location_code: string | null;
  start_date: string;
  due_date: string;
  reported_good_qty: string | number;
  reported_scrap_qty: string | number;
  short_line_count: number;
  note: string | null;
  revision: number;
  released_at: string | null;
  completed_at: string | null;
  closed_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  created_by_name: string | null;
  materials: MaterialLine[];
  operations: OperationLine[];
}

export interface ProductionOrderHeaderInput {
  qty?: string;
  start_date?: string;
  due_date?: string;
  warehouse_id?: string;
  bom_id?: string | null;
  routing_id?: string | null;
  note?: string | null;
  expected_revision?: number;
}

export interface OrderListParams {
  companyId: string;
  status: OrderStatusFilter;
  q: string;
  /** Column filters and sort (list RPC spec in @patronix/domain/list). */
  filters?: ListFilter[];
  sort?: ListSort | null;
}

export const ORDER_PAGE = 50;

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

export async function listProductionOrders(
  p: OrderListParams,
  cursor: string | null,
  signal: AbortSignal,
  size = ORDER_PAGE,
): Promise<Page<ProductionOrderRow>> {
  if (isDemoMode) return { rows: [], nextCursor: null };
  const { data, error } = await db()
    .rpc("production_order_page", {
      p_company_id: p.companyId,
      p_status: p.status,
      p_term: p.q.trim() || null,
      p_filters: p.filters ?? [],
      p_sort: sortParam(p.sort ?? null),
      p_after: cursor ? JSON.parse(cursor) : null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = data as (ProductionOrderRow & { sort_cursor: unknown })[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify(last.sort_cursor) : null };
}

async function getProductionOrder(id: string, signal: AbortSignal): Promise<ProductionOrderDocument | null> {
  if (isDemoMode) return null;
  const { data, error } = await db().rpc("production_order_document", { p_id: id }).abortSignal(signal);
  if (error) throw error;
  return (data as ProductionOrderDocument | null) ?? null;
}

export async function saveProductionOrder(id: string | null, companyId: string, itemId: string, header: ProductionOrderHeaderInput) {
  if (isDemoMode) throw new Error("Production orders are not available in demo mode");
  const { data, error } = await db().rpc("save_production_order", {
    p_id: id,
    p_company_id: companyId,
    p_item_id: itemId,
    p_header: header,
  });
  if (error) throw error;
  return data as { id: string; number: string; status: ProductionOrderStatus; revision: number };
}

export type OrderTransition = "release" | "reserve" | "cancel" | "complete" | "close";

export async function transitionProductionOrder(id: string, action: OrderTransition): Promise<{ short_qty?: string | number }> {
  if (isDemoMode) throw new Error("Production orders are not available in demo mode");
  const fn = {
    release: "release_production_order",
    reserve: "reserve_production_order",
    cancel: "cancel_production_order",
    complete: "complete_production_order",
    close: "close_production_order",
  }[action];
  const { data, error } = await db().rpc(fn, { p_id: id });
  if (error) throw error;
  return (data as { short_qty?: string | number } | null) ?? {};
}

export const orderKeys = {
  all: ["production", "orders"] as const,
  list: (p: OrderListParams) => [...orderKeys.all, "list", p] as const,
  detail: (id: string) => [...orderKeys.all, "detail", id] as const,
};

export function productionOrderListQuery(p: OrderListParams) {
  return infiniteQueryOptions({
    queryKey: orderKeys.list(p),
    queryFn: ({ pageParam, signal }) => listProductionOrders(p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 30_000,
    enabled: !!p.companyId,
  });
}

// Availability moves with every posting: shorter staleTime.
export function productionOrderDetailQuery(id: string) {
  return queryOptions({ queryKey: orderKeys.detail(id), queryFn: ({ signal }) => getProductionOrder(id, signal), staleTime: 15_000 });
}

const rules: [RegExp, string][] = [
  [/was changed by someone else/, "prd.err.stale"],
  [/is not an active manufactured item/, "prd.err.notManufactured"],
  [/^No approved BOM of .* is valid on/, "prd.err.noBom"],
  [/^The BOM must be an approved version/, "prd.err.bomVersion"],
  [/^The routing must be an approved version/, "prd.err.routingVersion"],
  [/^The quantity must be above zero/, "prd.err.qty"],
  [/^The due date cannot be before the start date/, "prd.err.dates"],
  [/^Warehouse not found in the company or inactive/, "po.err.warehouse"],
  [/^After release only the due date/, "prd.err.releasedLocked"],
  [/^This production order cannot be changed/, "prd.err.frozen"],
  [/^Only planned production orders can be released/, "prd.err.cannotRelease"],
  [/without postings can be cancelled|has postings/, "prd.err.cannotCancel"],
  [/^Only released or in-progress production orders can be completed/, "prd.err.cannotComplete"],
  [/^Only completed production orders can be closed/, "prd.err.cannotClose"],
  [/^A production order needs at least one material line/, "prd.err.noMaterials"],
  [/^Missing permission/, "prd.err.permission"],
  [/cannot be expressed in the base unit/, "documents.err.decimals"],
  [/^No unit conversion/, "documents.err.noConversion"],
];

export function prdErrorMessage(t: TFunction, error: unknown): string {
  const tr = t as unknown as (key: string) => string;
  const message = (error as { message?: string } | null)?.message ?? String(error ?? "");
  const rule = rules.find(([re]) => re.test(message));
  return rule ? tr(rule[1]) : message || tr("auth.errorGeneric");
}
